import React, { useEffect, useRef, useState } from 'react';
import Gantt from 'frappe-gantt';
import './frappe-gantt.css';

function toDate(v) {
  if (!v) return null;
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

function statusToClass(t) {
  if (t.status === 'done') return 'bar-done';
  if (t.status === 'cancelled') return 'bar-cancelled';
  if (t.is_overdue) return 'bar-overdue';
  if (t.status === 'in_progress') return 'bar-progress';
  return 'bar-new';
}

export default function ProjectGantt({ columns, project, onTaskClick }) {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);
  const [mode, setMode] = useState(() => localStorage.getItem('gantt_mode') || 'Week');
  const setModeAndSave = (m) => {
    setMode(m);
    localStorage.setItem('gantt_mode', m);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Собираем задачи из всех колонок
    const allTasks = columns.flatMap((c) => c.tasks);
    if (allTasks.length === 0) {
      containerRef.current.innerHTML = '';
      return;
    }

    const today = new Date().toISOString().slice(0, 10);

    const items = allTasks.map((t) => {
      const start = toDate(t.started_at) || toDate(t.created_at) || today;
      let end = toDate(t.due_date) || toDate(t.completed_at) || null;
      // Если срок не задан — рисуем полосу на 7 дней от старта, чтобы её было видно
      if (!end) {
        const d = new Date(start);
        d.setDate(d.getDate() + 7);
        end = d.toISOString().slice(0, 10);
      }
      // Для просроченных полоса должна тянуться до сегодняшнего дня
      if (t.is_overdue && end < today) end = today;
      // Минимум 1 день длительности
      if (end <= start) {
        const d = new Date(start);
        d.setDate(d.getDate() + 1);
        end = d.toISOString().slice(0, 10);
      }
      const progress = t.status === 'done' ? 100 : t.status === 'review' ? 70 : t.status === 'in_progress' ? 40 : 0;
      return {
        id: String(t.id),
        name: t.title,
        start,
        end,
        progress,
        custom_class: statusToClass(t),
      };
    });

    containerRef.current.innerHTML = '';
    ganttRef.current = new Gantt(containerRef.current, items, {
      view_mode: mode,
      language: 'ru',
      bar_height: 24,
      padding: 16,
      today_button: true,
      infinite_padding: false,
      on_click: (task) => {
        onTaskClick && onTaskClick(Number(task.id));
      },
      custom_popup_html: (task) => {
        return `
          <div class="gantt-popup">
            <div class="gantt-popup-title">${task.name}</div>
            <div class="gantt-popup-meta">${task.start} → ${task.end}</div>
            <div class="gantt-popup-meta">Прогресс: ${task.progress}%</div>
          </div>
        `;
      },
    });

    // Автоскролл к «сегодня» после рендера
    setTimeout(() => {
      try {
        if (ganttRef.current && typeof ganttRef.current.scroll_current === 'function') {
          ganttRef.current.scroll_current();
        }
      } catch (e) { /* ignore */ }
    }, 50);
  }, [columns, mode, onTaskClick]);

  const totalTasks = columns.reduce((s, c) => s + c.tasks.length, 0);

  return (
    <div className="gantt-wrap">
      <div className="gantt-toolbar">
        {['Day', 'Week', 'Month'].map((m) => (
          <button
            key={m}
            className={`tasks-btn ${mode === m ? 'primary' : ''}`}
            onClick={() => setModeAndSave(m)}
          >
            {m === 'Day' ? 'День' : m === 'Week' ? 'Неделя' : 'Месяц'}
          </button>
        ))}
        <div className="gantt-legend">
          <span><i className="dot dot-new"></i>Новая</span>
          <span><i className="dot dot-progress"></i>В работе</span>
          <span><i className="dot dot-done"></i>Готово</span>
          <span><i className="dot dot-overdue"></i>Просрочена</span>
        </div>
      </div>
      {totalTasks === 0 ? (
        <div className="tasks-empty">В проекте нет задач</div>
      ) : (
        <div className="gantt-container" ref={containerRef}></div>
      )}
    </div>
  );
}
